<template>
    <section class="cz2__steps-options__shared">
        <step-header class="cz2__body-step-header">
            <div
                :class="{ 'cz2__is-kiosk': isKiosk }"
                class="cz2__scrollbar-hide cz2__color-selector"
            >
                <span class="cz2__sr-only">
                    {{ $t('SR_ACTIVATING') }}
                </span>

                <h3 class="cz2__tone-tooltip">
                    <img
                        src="../assets/icons/funko-crown.svg"
                        aria-hidden="true"
                        class="cz2__crown-icon"
                    >
                    {{ $t('BODY_TONE_TOOLTIP') }}
                </h3>

                <div class="cz2__tone-wrapper">
                    <h2 class="cz2__tone-heading">
                        {{ $t('BODY_TONE_HEADING') }}
                    </h2>

                    <div
                        role="radiogroup"
                        :aria-label="$t('BODY_CHOOSE_TONE')"
                        class="cz2__color-selector-wrapper"
                    >
                        <div
                            v-for="color in colorHexValues"
                            :key="color.color"
                        >
                            <button
                                @click="selectColor(color)"
                                @keyup.space="selectColor(color)"
                                :class="[
                                    { selected: selectedColor && selectedColor.code === color.code },
                                ]"
                                role="radio"
                                :aria-checked="selectedColor && selectedColor.code === color.code"
                                :aria-label="color.name"
                            >
                                <div
                                    :style="{ background: `linear-gradient(#${color.color}, #${color.color})` }"
                                />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </step-header>

        <swiper
            key="bodyMenu"
            topQuerySelector=".selection-card"
            selectedQuerySelector=".selection-card--selected"
            class="cz2__steps-options__list-wrapper"
        >
            <selection-card-list
                ref="bodyTypeList"
            >
                <selection-card
                    v-for="body in bodyTypes"
                    :key="body.code"
                    :image="body.custom['fun-image-v2'] || body.custom['fun-image'] || body.custom.image"
                    :name="body.description[0].name"
                    :isSelected="isBodySelected(body)"
                    @select="selectBody(body)"
                />
            </selection-card-list>
        </swiper>
    </section>
</template>

<style lang="scss">
    @import "../style/variables.scss";

    #cz2.cz2 {
        .cz2__body-step-header {
            .cz2__color-selector {
                display: flex;
                flex-direction: column;
                gap: 12px;

                .cz2__color-selector-wrapper {
                    gap: 16px;

                    button {
                        width: 40px;
                        height: 40px;

                        @include respond-above(lg) {
                            width: 48px;
                            height: 48px;
                        }
                    }
                }
            }

            .cz2__tone-tooltip {
                font-family: 'Proxima Nova';
                font-size: 15px;

                display: flex;
                align-items: center;
                gap: 6px;

                .cz2__crown-icon {
                    width: 24px;
                    height: 18px;
                }
            }

            .cz2__tone-wrapper {
                display: flex;
                align-items: center;
                gap: 16px;

                .cz2__tone-heading {
                    text-transform: uppercase;
                    font-size: 22px;
                }
            }
        }
    }

    #cz2.cz2.cz2__is-kiosk {
        .cz2__body-step-header {
            .cz2__color-selector {
                gap: 0;

                .cz2__color-selector-wrapper {
                    gap: 26px;

                    button {
                        width: 72px;
                        height: 72px;
                    }
                }
            }

            .cz2__tone-tooltip {
                font-size: 20px;
                gap: 12px;

                .cz2__crown-icon {
                    width: 37px;
                    height: 28px;
                }
            }

            .cz2__tone-wrapper {
                gap: 26px;

                .cz2__tone-heading {
                    font-size: 28px;
                }
            }
        }
    }
</style>

<script>
    import Swiper from './ui/Swiper.vue';
    import * as constants from '../constants';

    import SelectionCard from './ui/SelectionCard.vue';
    import SelectionCardList from './ui/SelectionCardList.vue';
    import StepHeader from './ui/StepHeader.vue';

    export default {
        components: {
            SelectionCard,
            SelectionCardList,
            StepHeader,
            Swiper,
        },

        computed: {
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            colors() {
                if (this.viewController == null) {
                    return [];
                }

                const placement = this.viewController.findPlacement(constants.PLACEMENT_SKIN_COLOR);

                let colors = placement.components.filter((c) => !c.placeholder && this.viewController.isAvailable(c));

                colors = colors.filter((color) => {
                    if (color.custom['fun-heads']) {
                        const heads = color.custom['fun-heads'].split(',');

                        const allHeads = heads.some((head) => {
                            const headPlacement = this.viewController.findPlacement(constants.PLACEMENT_HEAD);

                            const headComponent = headPlacement.components.find((c) => c.custom['fun-sku'] === head);

                            if (headComponent == null) {
                                return false;
                            }

                            // eslint-disable-next-line
                            const code = headComponent.description[0].code;

                            const option = this.viewController.blueprint.options.find((o) => o.code === code);

                            let inventory = this.viewController.emit('getInventory', headComponent);

                            if (inventory == null) {
                                inventory = option && option.inventory;
                            }

                            if (this.$store.state.customizer.options.alwaysAvailable) {
                                inventory = 100;
                            }

                            const available = inventory > 0;

                            if (!available) {
                                this.$store.dispatch('log', {
                                    level: 'error',
                                    message: `Head ${headComponent.code} is not in stock`,
                                });
                            }

                            return available;
                        });

                        if (!allHeads) {
                            this.$store.dispatch('log', {
                                level: 'error',
                                message: `One of heads ${color.custom['fun-heads']} is not in stock`,
                            });

                            return false;
                        }
                    }

                    return true;
                });

                return colors;
            },

            colorHexValues() {
                return this.colors.map((color) => ({
                    color: color.custom.hex,
                    placementIndependentCode: color.placementIndependentCode,
                    code: color.code,
                    name: color.description[0].name,
                    component: color,
                }));
            },

            currentGroup() {
                return this.$store.state.customizer.currentGroup;
            },

            bodyPlacement() {
                return this.viewController.findPlacement(constants.PLACEMENT_BODY);
            },

            bodyTypes() {
                if (this.viewController == null) {
                    return [];
                }

                return this.bodyPlacement.components.filter((c) => !c.placeholder && this.viewController.isAvailable(c));
            },

            selectedColor() {
                if (this.viewController == null) {
                    return null;
                }

                const color = this.viewController.selectedAtPlacement(constants.PLACEMENT_SKIN_COLOR);

                return (color !== null && !color.placeholder) ? color : null;
            },

            selectedBodyType() {
                if (this.viewController == null) {
                    return null;
                }

                const body = this.viewController.selectedAtPlacement(constants.PLACEMENT_BODY);

                return (body !== null && !body.placeholder) ? body : null;
            },

            isFilled() {
                return this.selectedColor && this.selectedBodyType;
            },

            nextGroup() {
                return this.$store.getters.nextGroup;
            },

            headGroupSeen() {
                return this.$store.state.customizer.seenGroups.includes(constants.GROUP_HEAD);
            },

            skipLanding() {
                if (this.$store.state.options == null) {
                    return null;
                }

                return this.$store.state.options.skipLanding;
            },

            isKiosk() {
                return this.$store.state.customizer.options.isKiosk;
            },

            changeWarningShown() {
                return this.$store.state.changeWarningShown;
            },

            isFirstPop() {
                return this.viewController === this.$store.getters.popControllers[0];
            },

            hasBack() {
                return !this.isFirstPop || !this.skipLanding || this.isKiosk;
            },

            alertGroups() {
                return this.$store.state.customizer.alertGroups;
            },

            canGoBack() {
                if (!this.isFirstPop && this.alertGroups.length) {
                    return false;
                }

                return (!this.skipLanding && this.isFirstPop) || (this.isKiosk && this.isFirstPop) || !this.isFirstPop;
            },

            hasNext() {
                return !!this.nextGroup;
            },

            canGoNext() {
                return this.isFilled;
            },

            namePopModalWasShown() {
                return this.$store.state.namePopModalWasShown;
            },

            namePop2ModalWasShown() {
                return this.$store.state.namePop2ModalWasShown;
            },

            boxName() {
                return this.$store.state.customizer.boxName;
            },
        },

        watch: {
            viewController() {
                this.updateNavigation();
            },

            isFilled() {
                this.updateNavigation();
            },
        },

        mounted() {
            this.updateNavigation();

            this.$store.dispatch('addSeenGroup', this.currentGroup.code);

            this.showNamePopModal();
        },

        methods: {
            showAttachPacksInfoModal() {
                if (this.viewController.blueprint.custom['fun-can-show-attach-packs-info'] === 'true') {
                    if (!this.$store.state.attachPackInfoModalWasShown && this.$store.getters.attachPacks.length) {
                        this.$store.dispatch('showAttachPackInfoModal', true);
                        this.$store.dispatch('setAttachPackInfoModalWasShown', true);
                    }
                }
            },

            isBodySelected(body) {
                return this.selectedBodyType && this.selectedBodyType.code === body.code;
            },

            selectColor(hexColor) {
                const lastHead = this.viewController.selectedAtPlacement(constants.PLACEMENT_HEAD);

                const colorComponent = this.colors.filter((color) => color.placementIndependentCode === hexColor.placementIndependentCode)[0];

                if (!this.viewController.isAvailable(colorComponent)) {
                    return;
                }

                this.$store.dispatch('updateComponent', {
                    placement: constants.PLACEMENT_SKIN_COLOR,
                    code: colorComponent.code,
                }).then(() => {
                    this.pickHead(lastHead);
                }).catch(() => {
                });

                this.updateNavigation();
            },

            selectBody(body) {
                const lastHead = this.viewController.selectedAtPlacement(constants.PLACEMENT_HEAD);

                this.$store.dispatch('updateComponent', {
                    placement: constants.PLACEMENT_BODY,
                    code: body.code,
                }).then(() => {
                    this.pickHead(lastHead);
                }).catch(() => {
                });

                this.updateNavigation();
            },

            pickHead(lastHead) {
                const color = this.selectedColor;
                const skinTag = color.tags.find((t) => t.startsWith('skin-'));

                let bodyTag;

                if (lastHead && this.headGroupSeen) {
                    if (lastHead.tags) {
                        bodyTag = lastHead.tags.includes('head-male') ? 'head-male' : 'head-female';
                    }
                }

                if (bodyTag == null) {
                    const body = this.selectedBodyType;

                    if (body && body.tags) {
                        bodyTag = body.tags.includes('male') ? 'head-male' : 'head-female';
                    }
                }

                this.$store.dispatch('log', {
                    level: 'info',
                    message: `Selected color ${skinTag} and body ${bodyTag}`,
                });

                // Find a head.
                const heads = this.viewController.findPlacement(constants.PLACEMENT_HEAD);

                if (heads && heads.components) {
                    const head = heads.components.find((c) => {
                        if (c.tags.includes(skinTag) && c.tags.includes(bodyTag)) {
                            if (this.viewController.isAvailable(c)) {
                                return true;
                            }
                        }

                        return false;
                    });

                    if (head) {
                        this.$store.dispatch('log', {
                            level: 'info',
                            message: `Found a head ${head.code}`,
                        });

                        this.viewController.updateComponent(constants.PLACEMENT_HEAD, head.code);
                    }
                }

                // Check beards.
                const head = this.viewController.selectedAtPlacement(constants.PLACEMENT_HEAD);

                if (head && head.custom['fun-sku-beards']) {
                    const beardSku = head.custom['fun-sku-beards'];
                    const beardHead = this.viewController.findPlacement(constants.PLACEMENT_OTHER).components.find((c) => {
                        if (c.code.includes(beardSku)) {
                            return true;
                        }

                        return false;
                    });

                    if (beardHead) {
                        if (!this.viewController.isAvailable(beardHead)) {
                            const selectedBeard = this.viewController.selectedAtPlacement(constants.PLACEMENT_BEARD);
                            if (!selectedBeard.placeholder) {
                                const beardPlaceholder = this.viewController.findPlacement(constants.PLACEMENT_BEARD).components.find((x) => x.placeholder);

                                if (beardPlaceholder) {
                                    this.viewController.updateComponent(constants.PLACEMENT_BEARD, beardPlaceholder.code);
                                }
                            }
                        }
                    }
                }

                this.$store.dispatch('removeAlertGroup', {
                    code: 'Head',
                });
            },

            updateNavigation() {
                this.$emit('updateNavigation', {
                    hasBack: this.hasBack,
                    canGoBack: this.canGoBack,
                    hasNext: this.hasNext,
                    canGoNext: this.canGoNext,
                });
            },

            showNamePopModal() {
                if (!this.boxName) {
                    if (!this.namePopModalWasShown) {
                        this.$store.dispatch('showNamePopModal', true);
                        this.$store.dispatch('setNamePopModalWasShown', true);
                    } else if (!this.isFirstPop && !this.namePop2ModalWasShown) {
                        this.$store.dispatch('showNamePopModal', true);
                        this.$store.dispatch('setNamePop2ModalWasShown', true);
                    }
                }
            },
        },
    };
</script>
